import {
  Clazz,
  GenderScope,
  HomeworkPlan,
  HomeworkItem,
  HomeworkGroup,
  HomeworkStages,
  HomeworkContent,
  HomeworkPlanForm,
  HomeworkRepeatBy,
  HomeworkStageKey,
  HomeworkStageMap,
  HomeworkReleaseType,
  HomeworkTemplateType,
  IntelligentHomeworkStages,
  IntelligentHomeworkStageMap,
} from 'kuaidong-model'

import sortBy from 'lodash/sortBy'
import groupBy from 'lodash/groupBy'
import isToday from 'date-fns/isToday'
import mapValues from 'lodash/mapValues'
import { TeacherService } from './TeacherService'
import { globalConfigStore } from 'kuaidong-store'
import { DateWrapper, DateUtil, Weekday, StringUtil } from 'kuaidong-lib'

export const REPEAT_BY_OPTIONS = [
  { value: 'day', label: '每天重复' },
  { value: 'week', label: '每天不同，按周重复' },
  { value: 'weekend', label: '仅周末' },
]

export const REPEAT_BY_OPTIONS_FOR_INTELLIGENT = [
  { value: 'week', label: '按周布置' },
  { value: 'weekends_only', label: '仅周末' },
]

export interface HomeworkPlanGroup {
  clazz: Clazz
  homeworkPlans: HomeworkPlan[]
  winterHolidayPlan: HomeworkPlan | null
}

export function hasRepetitionTip(plan: HomeworkPlan): boolean {
  return plan.group.is_check_wd5
}

export function inferRepeatBy(homeworkContents: HomeworkContent[], isHomeworkIntelligent = false): HomeworkRepeatBy {
  if (isHomeworkIntelligent) {
    if (homeworkContents.length > 2) {
      return 'week'
    } else {
      return 'weekends_only'
    }
  }

  if (!homeworkContents.length) {
    return 'day'
  } else if (homeworkContents.length === 1 && !homeworkContents[0].day_of_week) {
    return 'day'
  } else {
    return 'week'
  }
}

export function calcRepeatBy(
  homeworkPlan: Pick<HomeworkPlan, 'homework_contents'> & { group?: HomeworkGroup },
  isHomeworkIntelligent = false
) {
  return homeworkPlan.group?.repeat_type || inferRepeatBy(homeworkPlan.homework_contents, isHomeworkIntelligent)
}

export function formatStartDate(startDate: string): string {
  const dateWrapper = new DateWrapper(startDate)

  const dateStr = isToday(dateWrapper.date) ? '今天' : dateWrapper.showDateStr
  const weekStr = dateWrapper.showWeekDayShort

  return `${dateStr}（${weekStr}）`
}

export function formatEndDate(endDate: string): string {
  return new DateWrapper(endDate).showDateStr
}

export function formatRepeatBy(repeatBy: HomeworkRepeatBy, isHomeworkIntelligent = false): string {
  if (isHomeworkIntelligent) {
    return REPEAT_BY_OPTIONS_FOR_INTELLIGENT.find((o) => o.value === repeatBy)!.label
  } else {
    return REPEAT_BY_OPTIONS.find((o) => o.value === repeatBy)!.label
  }
}

export function formatMessages(messages: string[]): string {
  return messages.join('\n').trim()
}

export function formattedStartDate(plan: HomeworkPlan): string {
  return formatStartDate(plan.group.start_time)
}

export function formattedEndDate(plan: HomeworkPlan): string {
  return formatEndDate(plan.group.expire_time)
}

export function formattedTeacherName(plan: HomeworkPlan): string {
  return TeacherService.buildName(plan.group.teacher?.teacher_name)
}

export function formattedRepeatBy(
  plan: Pick<HomeworkPlan, 'homework_contents'>,
  isHomeworkIntelligent = false
): string {
  const repeatBy = calcRepeatBy(plan, isHomeworkIntelligent)
  return formatRepeatBy(repeatBy, isHomeworkIntelligent)
}

export function formattedMessages(plan: HomeworkPlan): string {
  return formatMessages(plan.group.messages)
}

export function differInGender(homeworkContents: HomeworkContent[]) {
  return homeworkContents.some((c) => {
    return c.homework_items.some((i) => i.gender_scope !== 'ALL')
  })
}

export function homeworkContentsByGender(homeworkContents: HomeworkContent[], genderScope: GenderScope | null = null) {
  return homeworkContents
    .map((c) => {
      return {
        ...c,
        homework_items: c.homework_items.filter((i) => {
          if (!genderScope || !i.gender_scope || i.gender_scope === 'ALL') {
            return true
          }
          return i.gender_scope === genderScope
        }),
      }
    })
    .filter((c) => c.homework_items.length)
}

export function orderedHomeworkContents(homeworkContents: HomeworkContent[], genderScope: GenderScope | null = null) {
  const items = homeworkContentsByGender(homeworkContents, genderScope)
  return sortBy(items, 'day_of_week')
}

export function oneOfReleaseId(plan: HomeworkPlan): number | null {
  if (!plan.homework_contents.length) {
    return null
  }
  return plan.homework_contents[0]?.release_id || null
}

export function isWinterHolidayPlan(plan: HomeworkPlan): boolean {
  return plan.group.release_type === 'winter_holiday'
}

export function formatWinterHolidayDuration(startTime: string, expireTime: string) {
  const startDateStr = new DateWrapper(startTime).showDateStr
  const endDateStr = new DateWrapper(expireTime).showDateStr

  return `${startDateStr} 至 ${endDateStr}`
}

export function formattedWinterHolidayDuration(plan: HomeworkPlan): string {
  return formatWinterHolidayDuration(plan.group.start_time, plan.group.expire_time)
}

export function hasWinterHolidayInGroup(group: HomeworkPlanGroup): boolean {
  return !!group.winterHolidayPlan
}

export function formatHomeworkReleaseType(
  releaseType: HomeworkReleaseType,
  isHomeworkIntelligent = false,
  isLongTermHomeworkId = false
) {
  switch (releaseType) {
    case 'normal':
      return isHomeworkIntelligent ? '智能作业' : isLongTermHomeworkId ? '普通作业' : '普通作业'
    case 'summer_holiday':
    case 'activity_summer_2022':
      return '暑假作业'
    case 'winter_holiday':
      return '寒假作业'
    case 'activity_body_exam':
      return '体测练习'
  }
}

export function formatHomeworkTemplateType(templateType: HomeworkTemplateType) {
  switch (templateType) {
    case HomeworkTemplateType.Official:
      return '官方模版'
    case HomeworkTemplateType.Custom:
      return '自定义模版'
    default:
      throw Error('Wrong homework template type.')
  }
}

function homeworkContentsContainWeekends(form: HomeworkPlanForm): boolean {
  return form.homeworkContents
    .filter((c) => c.day_of_week === 6 || c.day_of_week === 7)
    .some((c) => c.homework_items.length > 0)
}

export function fridayOverWeekend(form: HomeworkPlanForm): boolean {
  if (form.repeatBy === 'day') {
    return false
  }

  return !homeworkContentsContainWeekends(form)
}

export function buildHomeworkPlanGroups(homeworkPlans: HomeworkPlan[]): HomeworkPlanGroup[] {
  const homeworkPlanById = groupBy(homeworkPlans, 'clazz.id')
  const groups = mapValues(homeworkPlanById, (homeworkPlans: any) => ({
    clazz: homeworkPlans[0].clazz,
    homeworkPlans: homeworkPlans,
    winterHolidayPlan: homeworkPlans.find(isWinterHolidayPlan) || null,
  }))
  return sortBy(groups, ['clazz.grade_no', 'clazz.class_no'])
}

export function findHomeworkContent(homeworkContents: HomeworkContent[], weekday: Weekday): HomeworkContent | null {
  return homeworkContents.find((c) => c.day_of_week === weekday) || null
}

export function formatHomeworkStage(stage?: string | null) {
  if (stage) {
    return HomeworkStageMap[stage as HomeworkStageKey] || HomeworkStages[1]
  } else {
    return HomeworkStages[1]
  }
}

export function formatIntelligentHomeworkStage(stage?: string | null) {
  if (stage) {
    return IntelligentHomeworkStageMap[stage as HomeworkStageKey] || IntelligentHomeworkStages[1]
  } else {
    return IntelligentHomeworkStages[1]
  }
}

export function buildHomeworkStages(homeworkContent?: HomeworkContent) {
  if (homeworkContent) {
    let count = 1

    const stageItemsMap = groupBy(homeworkContent.homework_items, (item) => formatHomeworkStage(item.stage))

    return HomeworkStages.map((stage) => ({
      name: stage,
      homeworkItems: (stageItemsMap[stage] || []).map((item) => ({
        ...item,
        no: StringUtil.padZero(count++, 2),
      })),
    })).filter((stage) => stage.homeworkItems.length)
  }

  return []
}

export function filterHomeworkItemRest(homeworkItems: HomeworkItem[]) {
  return homeworkItems.filter((item) => !globalConfigStore.isRestDrill(item))
}

export function calcHomeworkContentDurationInSecond(
  homeworkContent: Pick<HomeworkContent, 'homework_items'>,
  options: { masterType?: string } = {}
) {
  return homeworkContent.homework_items
    .filter((x) => !options.masterType || options.masterType === x.master_type) // 如果传了类型则过滤出来
    .reduce((result, item) => {
      const drill = globalConfigStore.findDrillById(item.drill_id)
      if (drill && !globalConfigStore.isRestDrill(drill)) {
        result += (drill.duration_seconds || 0) * item.repeats
      }
      return result
    }, 0)
}

export function calcHomeworkPlanDurationInSecond(homeworkPlan: Pick<HomeworkPlan, 'homework_contents'>) {
  const result = homeworkPlan.homework_contents.reduce((result, content) => {
    return result + calcHomeworkContentDurationInSecond(content)
  }, 0)

  return calcRepeatBy(homeworkPlan) === 'day' ? result * 7 : result
}

export function calcHomeworkPlansDurationInSecond(homeworkPlans: Pick<HomeworkPlan, 'homework_contents'>[]) {
  return homeworkPlans.reduce((result, plan) => {
    return result + calcHomeworkPlanDurationInSecond(plan)
  }, 0)
}

export function calcHomeworkContentEquipments(homeworkContent: HomeworkContent) {
  const result = homeworkContent.homework_items.reduce((result: string[], item) => {
    const drill = globalConfigStore.findDrillById(item.drill_id)
    if (drill && drill.equipments) {
      return [...result, ...drill.equipments]
    }
    return result
  }, [])

  return [...new Set(result)]
}

export function calcHomeworkPlanEquipments(homeworkPlan: Pick<HomeworkPlan, 'homework_contents'>) {
  const result = homeworkPlan.homework_contents.reduce((result: string[], content) => {
    return [...result, ...calcHomeworkContentEquipments(content)]
  }, [])

  return [...new Set(result)]
}

export function calcHomeworkPlansEquipments(homeworkPlans: Pick<HomeworkPlan, 'homework_contents'>[]) {
  const result = homeworkPlans.reduce((result: string[], plan) => {
    return [...result, ...calcHomeworkPlanEquipments(plan)]
  }, [])

  return [...new Set(result)]
}

export function calcDrillNum(
  homeworkPlan: Pick<HomeworkPlan, 'homework_contents'>,
  options: { masterType?: string } = {}
) {
  return homeworkPlan.homework_contents.reduce((result: number, content) => {
    let items = content.homework_items
    if (options.masterType) {
      items = items.filter((x) => x.master_type === options.masterType)
    }
    result += filterHomeworkItemRest(items).length
    return result
  }, 0)
}

export function calcDrillRepeats(
  homeworkPlan: Pick<HomeworkPlan, 'homework_contents'>,
  options: { masterType?: string } = {}
) {
  return homeworkPlan.homework_contents.reduce((result: number, content) => {
    let items = content.homework_items
    if (options.masterType) {
      items = items.filter((x) => x.master_type === options.masterType)
    }
    result += filterHomeworkItemRest(items).reduce((repeats: number, item) => {
      repeats += item.repeats
      return repeats
    }, 0)
    return result
  }, 0)
}

export function calcSummary(homeworkPlan: Pick<HomeworkPlan, 'homework_contents'>, weekday: Weekday) {
  const repeatBy = calcRepeatBy(homeworkPlan)

  let content: HomeworkContent | undefined = homeworkPlan.homework_contents[0]
  if (repeatBy !== 'day') {
    content = homeworkPlan.homework_contents.find((content) => content.day_of_week === weekday)
  }

  return {
    totalMinutes: DateUtil.second2Minute(calcHomeworkPlanDurationInSecond(homeworkPlan)),
    totalMinutesCurDate: content ? DateUtil.second2Minute(calcHomeworkContentDurationInSecond(content)) : 0,
    totalItemNumCurDate: content ? calcDrillNum({ homework_contents: [content] }) : 0,
    totalItemRepeatsCurDate: content ? calcDrillRepeats({ homework_contents: [content] }) : 0,
    totalEquipments: content ? calcHomeworkPlanEquipments({ homework_contents: [content] }) : [],
  }
}

export function buildSummaryItems(homeworkPlan: Pick<HomeworkPlan, 'homework_contents'>, weekday: Weekday) {
  const summary = calcSummary(homeworkPlan, weekday)

  return [
    {
      label: '本周运动总时长',
      value: summary.totalMinutes,
      unit: '分钟',
    },
    {
      label: '当日运动总时长',
      value: summary.totalMinutesCurDate,
      unit: '分钟',
    },
    {
      label: '当日运动项目数',
      value: summary.totalItemNumCurDate,
      unit: '个',
    },
    {
      label: '当日运动项组数',
      value: summary.totalItemRepeatsCurDate,
      unit: '组',
    },
    {
      label: '运动器材准备',
      value: summary.totalEquipments.join('、'),
      unit: '',
    },
  ]
}

// migrate from AHomeworkIntelligent.ts
export async function buildIntelligentPlan(res: any) {
  await globalConfigStore.ensureConfigIsLoaded()

  return {
    stat: {
      weekDuration: +(res.stat.week_duration_in_secs / 60).toFixed(1),
      duration: +(res.stat.day_duration_in_secs / 60).toFixed(1),
      count: res.stat.day_repeats,
      drillCount: res.stat.day_drills,
    },
    stages: res.phases.map((v: any) => ({
      title: formatIntelligentHomeworkStage(v.phase.title),
      duration: +(
        res.homework_items
          .filter((d: any) => d.stage === v.phase.title)
          .reduce((p: number, d: any) => p + d.training_duration, 0) / 60
      ).toFixed(1),
      items: res.homework_items
        .filter((d: any) => d.stage === v.phase.title)
        .map((d: any) => ({
          cat: globalConfigStore.findContainerType(d.container_type)?.title,
          name: globalConfigStore.findDrill(d.container_type, d.drill_id)?.title,
          count: d.repeats,
        })),
    })),
  }
}
